import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { TimeZoneDataCollectionProvider } from '@nts/std/timezone';
import { TimeZoneData } from '@nts/std/timezone';
import { BigNumber, CompareModes, OpenDirections } from '@nts/std/types';
import { DateTimeOffset } from '@nts/std/types';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { PropertyViewModel } from '../property-view-model';
import { PropertyViewModelInitializationInfo } from '../property-view-model-initialization-info';
import { DateTimeMetaDataInterface, ViewModelInterface } from '@nts/std/interfaces';
;

export abstract class BaseDateTimeOffsetPropertyViewModel extends PropertyViewModel<DateTimeOffset> {

  private readonly MASKDATEANDTIME: string = 'g'; // General date/time pattern(short time)
  private readonly MASKDATE_DEFAULT: string = 'd'; // d	Short date pattern

  metadataMaxValue: Date;
  metadataMinValue: Date;
  private internalShowTime = false;
  showTime$ = new BehaviorSubject<boolean>(this.internalShowTime);
  showTimeZone = false;
  advancingCaret = true;
  timeZones: Array<TimeZoneData>;
  currentOffset: number;

  get isTimeZoneEnabled(): boolean {
    return this._isTimeZoneEnabled && this.isEnabled;
  }
  set isTimeZoneEnabled(value: boolean) {
    if (this._isTimeZoneEnabled !== value) {
      this.isTimeZoneEnabled = value;
    }
  }

  get showTime(): boolean {
    return this.internalShowTime;
  }
  set showTime(value: boolean) {
    if (this.internalShowTime !== value) {
      this.internalShowTime = value;
      this.onPropertyChanged('showTime');
      this.showTime$.next(value);
    }
  }

  get editMask(): string {
    if (this._editMask) {
      return this._editMask;
    }

    if (this.showTime) {
      return this.MASKDATEANDTIME;
    } else {
      return this.MASKDATE_DEFAULT;
    }
  }
  set editMask(value: string) {
    this._editMask = value;
  }

  private _editMask: string;
  private _isTimeZoneEnabled = true;

  #compareMode: CompareModes | null = null;
  compareMode$: BehaviorSubject<CompareModes | null> = new BehaviorSubject<CompareModes | null>(this.#compareMode);

  get compareMode(): CompareModes | null {
    return this.#compareMode;
  }
  set compareMode(value: CompareModes | null) {
    if (this.#compareMode !== value) {
      this.#compareMode = value;
      this.onPropertyChanged('compareMode');
      this.compareMode$.next(value);
    }
  }

  #comparePropertyName: string | null = null;
  comparePropertyName$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(this.#comparePropertyName);

  get comparePropertyName(): string | null {
    return this.#comparePropertyName;
  }
  set comparePropertyName(value: string | null) {
    if (this.#comparePropertyName !== value) {
      this.#comparePropertyName = value;
      this.onPropertyChanged('comparePropertyName');
      this.comparePropertyName$.next(value);
    }
  }

  #openDirection: OpenDirections | null = null;
  openDirection$: BehaviorSubject<OpenDirections | null> = new BehaviorSubject<OpenDirections | null>(this.#openDirection);

  get openDirection(): OpenDirections | null {
    return this.#openDirection;
  }
  set openDirection(value: OpenDirections | null) {
    if (this.#openDirection !== value) {
      this.#openDirection = value;
      this.onPropertyChanged('openDirection');
      this.openDirection$.next(value);
    }
  }

  #minValue: DateTimeOffset | null = null;
  minValue$: BehaviorSubject<DateTimeOffset | null> = new BehaviorSubject<DateTimeOffset | null>(this.#minValue);
  get minValue(): DateTimeOffset | null {
    return this.#minValue;
  }
  set minValue(value: DateTimeOffset | null) {
    if (this.#minValue !== value) {
      this.#minValue = value;
      this.onPropertyChanged('minValue');
      this.minValue$.next(value);
    }
  }

  #maxValue: DateTimeOffset | null = null;
  maxValue$: BehaviorSubject<DateTimeOffset | null> = new BehaviorSubject<DateTimeOffset | null>(this.#maxValue);
  get maxValue(): DateTimeOffset | null {
    return this.#maxValue;
  }
  set maxValue(value: DateTimeOffset | null) {
    if (this.#maxValue !== value) {
      this.#maxValue = value;
      this.onPropertyChanged('maxValue');
      this.maxValue$.next(value);
    }
  }

  constructor(initInfo: PropertyViewModelInitializationInfo, valueIsNullable: boolean) {
    super(initInfo, valueIsNullable);

    this.timeZones = new Array<TimeZoneData>();

    this.currentOffset = - (new Date()).getTimezoneOffset();

    this.compareMode = (initInfo.propertyMetaData as DateTimeMetaDataInterface).compareMode;
    this.comparePropertyName = (initInfo.propertyMetaData as DateTimeMetaDataInterface).comparePropertyName;
    this.openDirection = (initInfo.propertyMetaData as DateTimeMetaDataInterface).openDirection;
    this.minValue = (initInfo.propertyMetaData as DateTimeMetaDataInterface).minValue as DateTimeOffset;
    this.maxValue = (initInfo.propertyMetaData as DateTimeMetaDataInterface).maxValue as DateTimeOffset;

    if (this.comparePropertyName != null) {
      this.subscribeToComparePropertyChanges(this.comparePropertyName);
    }
  }

  private subscribeToComparePropertyChanges(comparePropertyName: string) {
    const parentViewModel = this.parent;

    const subscriptionFn = () => new Promise<void>((resolve) => {
      const comparePropertyViewModel = parentViewModel.getProperty(comparePropertyName);

      if (comparePropertyViewModel) {
        comparePropertyViewModel
          .propertyChanged
          .pipe(
            takeUntil(this.destroySubscribers$)
          )
          .subscribe(() => {
            this.validate();
          });
      }

      resolve();
    });

    parentViewModel.appendToPostInit(subscriptionFn);
  }

  override setValue(value: DateTimeOffset) {

    // LogService.debug('corrente: ', DateTimeOffset.formatToString(value));
    // LogService.debug('old: ', DateTimeOffset.formatToString(this.value));
    // LogService.debug('toUTCString è:', value.dateTime.toUTCString());
    // LogService.debug('offset è:', value.offset);
    // LogService.debug('old offset è:', this.value.offset);
    if (DateTimeOffset.formatToString(value) !== DateTimeOffset.formatToString(this.value)) {
      super.setValue(value);
    }

  }

  /**
       * Ritorna il valore minimo calcolato per questa proprietà in base alla proprietà di confronto e al minimo
       * valore.
       */
  getComputedMinValue(): DateTimeOffset | null {
    if (this.compareMode == null && this.minValue == null) {
      return null;
    }

    let minValue: DateTimeOffset | null = this.minValue;

    if (this.compareMode != null) {
      const compareProperty = this.parent as ViewModelInterface;
      const comparePropertyViewModel = compareProperty?.getProperty(this.comparePropertyName);
      if (comparePropertyViewModel == null || comparePropertyViewModel.value == null) {
        return minValue;
      }

      if (this.compareMode === CompareModes.GreaterThanOrEqualTo) {
        const minDateFromCompareMode = comparePropertyViewModel.value ? comparePropertyViewModel.value : null;
        return (minValue && minValue > minDateFromCompareMode) ? minValue : minDateFromCompareMode;
      }

      // TODO: da rivedere
      // if (this.compareMode === CompareModes.GreaterThan) {
      //   const minDateFromCompareMode = comparePropertyViewModel.value ? comparePropertyViewModel.value).add(1, 'days').toDate() : null;
      //   return (minValue && minValue > minDateFromCompareMode) ? minValue : minDateFromCompareMode;
      // }
    }

    return minValue;
  }

  /**
   * Ritorna il valore massimo calcolato per questa proprietà in base alla proprietà di confronto e al massimo
   * valore.
   */
  getComputedMaxValue(): DateTimeOffset | null {
    if (this.compareMode == null && this.maxValue == null) {
      return null;
    }

    let maxValue: DateTimeOffset | null = this.maxValue;
    if (this.compareMode != null) {
      const compareProperty = this.parent as ViewModelInterface;
      const comparePropertyViewModel = compareProperty?.getProperty(this.comparePropertyName);
      if (comparePropertyViewModel == null || comparePropertyViewModel.value == null) {
        return maxValue;
      }

      if (this.compareMode === CompareModes.LessThanOrEqualTo) {
        const maxDateFromCompareMode = comparePropertyViewModel.value ? comparePropertyViewModel.value : null;
        return (maxValue && maxValue < maxDateFromCompareMode) ? maxValue : maxDateFromCompareMode;
      }

      // TODO: da rivedere
      // if (this.compareMode === CompareModes.LessThan) {
      //   const maxDateFromCompareMode = comparePropertyViewModel.value ? moment(comparePropertyViewModel.value).subtract(1, 'days').toDate() : null;
      //   return (maxValue && maxValue < maxDateFromCompareMode) ? maxValue : maxDateFromCompareMode;
      // }
    }

    return maxValue;
  }

  setDefaultValueFromLayoutMetaData(value: string | number | BigNumber | boolean) {
    // TODO
  }

  findTimeZone(timeSpan: number) {
    return this.timeZones.find(t =>
      t.timeSpan === timeSpan);
  }

  async populateTimeZonesAsync(http: HttpClient) {
    const timeZones = await TimeZoneDataCollectionProvider.getTimeZoneDataCollection(http);

    timeZones.forEach(tz => {
      this.timeZones.push(new TimeZoneData(tz.key, tz.timeZoneInfoId, tz.timeSpan, tz.description));
    });
  }

  protected getFormattedValue(
    value?: DateTimeOffset,
    showTime?: boolean,
    showTimeZone?: boolean
  ): string {

    // Attenzione value.dateTime arriva con l'orario indicato dal server ma con il timezone locale

    value = value ?? this.value;
    showTime = showTime ?? this.showTime;
    showTimeZone = showTimeZone ?? this.showTimeZone;

    let formattedValue: string;

    if (value?.dateTime != null) {
      if (showTimeZone) {

        // Se visualizzo il time zone stampo direttamente a video quello che mi da il server
        formattedValue = moment(value.dateTime).clone().utcOffset(value.offset).format('L');
        if (showTime) {
          formattedValue += ' ' + moment(value.dateTime).clone().utcOffset(value.offset).format('LT');
        }
        formattedValue += ' ' + moment().utcOffset(value.offset as number).format('Z');
        return formattedValue;

      } else {

        // Se non voglio il timezone devo calcolare l'ora locale
        const momentDate = moment(value.dateTime).clone();

        formattedValue = momentDate.local().format('L');
        if (showTime) {
          formattedValue += ' ' + moment(momentDate).local().format('LT');
        }
        return formattedValue;
      }
    } else {
      return '';
    }
  }

  getOffset() {
    return this.currentOffset;
  }
}

export class DateTimeOffsetPropertyViewModel extends BaseDateTimeOffsetPropertyViewModel {

  constructor(initInfo: PropertyViewModelInitializationInfo) {
    super(initInfo, true);
    this.setFaultBackValueFunc(() => new DateTimeOffset(new Date(), this.getOffset()));
  }

  override async resetValue(useDefaultValue: boolean): Promise<void> {
    if (useDefaultValue) {
      await this.setValueAsync(new DateTimeOffset(new Date(), this.getOffset()));
    } else {
      await this.setValueAsync(this.faultBackValue);
    }
  }

  override setValue(value: DateTimeOffset | null) {
    if (value?.dateTime != null) {
      // Se uno seleziona solo la data, imposto timezone corrente di default
      value.timeZoneData = this.findTimeZone(value.offset != null ? value.offset : this.getOffset());
    } else {
      value = null;
    }

    super.setValue(value);
  }
}

export class NDateTimeOffsetPropertyViewModel extends BaseDateTimeOffsetPropertyViewModel {

  constructor(initInfo: PropertyViewModelInitializationInfo) {
    super(initInfo, true);
    this.isNullable = true;
  }

  override setValue(value: DateTimeOffset | null) {
    if (value?.dateTime != null) {
      if (value.timeZoneData == null) {
        // Se uno seleziona solo la data, imposto timezone corrente di default
        value.timeZoneData = this.findTimeZone(value.offset != null ? value.offset : this.getOffset());
      }
    } else {
      value = null;
    }

    super.setValue(value);
  }
}
